import React, {useState} from 'react';
import {
  createStyles,
  Dialog,
  DialogProps,
  Divider,
  IconButton,
  makeStyles,
  Tooltip
} from '@material-ui/core';
import {CloseOutlined as CloseIcon} from '@material-ui/icons';
import CardPaymentDashboard from 'src/modules/payments/CardPaymentDashboard/CardPaymentDashboard';
import {Trans, useTranslation} from 'react-i18next';
import ThanksScreen from 'src/modules/payments/PaymentSuccess/ThanksScreen';
import {BASE_DETAILS, GUARANTOR_LOGIN} from '@shared/store/actions/Index';
import {useDispatch, useSelector} from 'react-redux';
import {paymentMethodTypes} from '@shared/constants/Mapping';
import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog';
import {cancelCardMachineTransaction} from '@shared/services/PaymentProvider.service';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {CommunicationService} from 'src/service/CommunicationService';

// FIXME: updated the types from 'any' to proper type
interface CardPayDialogProps extends DialogProps {
  clinicId: string | number;
  isAdhoc?: boolean;
  handleClose?(reason: 'cancel' | 'success'): void;
  clinicGroupId: string | number;
  guarantorId: string | number;
  guarantorName: string;
  guarantorDetails?: any;
  collectCopay?: boolean;
}

const useStyles = makeStyles(() =>
  createStyles({
    dialogTitle: {
      padding: '16px 24px',
      fontWeight: 700,
      fontSize: '1.125rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    root: {
      '& .MuiDialog-paperFullWidth': {
        height: '100%'
      }
    }
  })
);

const CardPayDialog = (props: CardPayDialogProps): React.ReactElement => {
  const {
    isAdhoc,
    guarantorName,
    clinicId,
    clinicGroupId,
    guarantorId,
    guarantorDetails,
    handleClose,
    collectCopay,
    ...rest
  } = props;
  const {enqueueSnackbar} = useSnackbar();
  const cardMachineDetailsObject: any = useSelector(
    (state: any) =>
      state?.cardMachine?.cardMachineDetailsObject?.cardMachineDetailsObject ||
      null
  );

  const cardMachinePayment: any = useSelector(
    (state: any) => state.cardMachine.cardMachineReferenceId || null
  );
  const classes = useStyles();
  const {t} = useTranslation();
  const dispatch = useDispatch();
  // states
  const [thanksScreen, showThanksScreen] = React.useState<boolean>(false);
  const [confirmationDialog, setConfirmationDialog] = React.useState<boolean>(
    false
  );
  const [cardMachineDetails, setCardMachineDetails] = React.useState<any>({});
  const [
    cancelCardMachinePayment,
    setCancelCardMachinePayment
  ] = useState<boolean>(false);
  const [paymentOption, setPaymentOption] = React.useState<string>(
    paymentMethodTypes.PAY_NOW
  );
  React.useEffect(() => {
    dispatch({
      type: BASE_DETAILS,
      payload: {
        data: {clinicId, clinicGroupId, guarantorId, guarantorDetails}
      }
    });
    dispatch({type: GUARANTOR_LOGIN, payload: {isLogin: true}});
    setCardMachineDetails({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setCardMachineDetails(cardMachineDetailsObject);
    setCancelCardMachinePayment(false);
  }, [cardMachineDetailsObject]);

  // API to cancel the transaction
  const cancelCardMachineTransactionPayment = (data: any) => {
    if (cardMachinePayment?.cardMachineReferenceId !== null) {
      cancelCardMachineTransaction({
        clinic_group_id: data?.clinic_group_id,
        clinic_id: data?.clinic_id,
        body: {
          ...data,
          terminal_id: data?.terminal_id,
          reference_id: cardMachinePayment?.cardMachineReferenceId
        }
      })
        .then(res => {
          setCancelCardMachinePayment(false);
          setConfirmationDialog(false);
          enqueueSnackbar(res?.message, SnackBarConfig('s'));
        })
        .catch((error: any) => {
          setCancelCardMachinePayment(false);

          enqueueSnackbar(
            (error && error.message) ||
              t('PAYMENTS_LOGIN.TRANSACTION_NOT_INITIATED'),
            SnackBarConfig('e')
          );
        });
    }
  };

  return (
    <div>
      <Dialog
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth={'sm'}
        className={classes.root}
        fullWidth
        {...rest}>
        <div
          className={classes.dialogTitle}
          data-test-id="pcl-payments-cardDetailsDialogTitle">
          {t(
            paymentOption === paymentMethodTypes.PAY_NOW ||
              paymentOption === paymentMethodTypes.PAY_CASH
              ? 'PAY_DIALOG.TITLE'
              : 'PAY_DIALOG.PAYMENT_PLAN',
            {guarantorName}
          )}
          <Tooltip title={t('COMMON.CANCEL').toString()}>
            <IconButton
              data-test-id="pcl-payments-closeDialog"
              onClick={() => {
                if (typeof handleClose === 'function') {
                  if (
                    cardMachineDetails?.status === 'active' &&
                    confirmationDialog
                  ) {
                    // showing confirmation dialog on cancel icon click
                    setCancelCardMachinePayment(true);
                  } else {
                    handleClose(thanksScreen ? 'success' : 'cancel');
                    setConfirmationDialog(false);
                  }
                  // Trigger an event when guarantor payment is completed
                  CommunicationService.sendEvent({
                    action: 'GUARANTOR_PAYMENT_COMPLETED',
                    data: null
                  });
                }
              }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </div>
        <Divider />
        {thanksScreen ? (
          <ThanksScreen
            data-test-id="pcl-payments-amountPaidReferencePopUp"
            history={null}
            match={{}}
            isAdmin={true}
            clinicId={clinicId}
            clinicGroupId={clinicGroupId}
            guarantorId={guarantorId}
          />
        ) : (
          <CardPaymentDashboard
            paymentCompleted={(): void => {
              showThanksScreen(true);
            }}
            history={null}
            match={{}}
            isAdmin={true}
            isAdhoc={!!isAdhoc}
            // setLoader={setLoader}
            updatePaymentPlan={(paymentType: string) => {
              setPaymentOption(paymentType);
            }}
            setConfirmationDialog={setConfirmationDialog}
            collectCopay={collectCopay}
          />
        )}
      </Dialog>

      {/* confirmation dialog */}
      {cancelCardMachinePayment &&
        cardMachineDetails?.status === 'active' &&
        confirmationDialog && (
          <ConfirmationDialog
            open={cancelCardMachinePayment}
            title={t('INSTALLMENTS.CANCEL_CARD_MACHINE_TRANSACTION')}
            description={
              <Trans
                i18nKey={'INSTALLMENTS.DELETE_TRANSCATION'}
                values={{
                  machineName: cardMachineDetails?.machine_name
                }}
                components={[<strong key={'X'} />]}
              />
            }
            confirmButtonText={t('INSTALLMENTS.YES')}
            cancelButtonText={t('INSTALLMENTS.NO')}
            onClose={response => {
              if (response === 'confirm') {
                setCancelCardMachinePayment(true);
                cancelCardMachineTransactionPayment(cardMachineDetails);
              } else {
                setCancelCardMachinePayment(false);
              }
            }}
          />
        )}
    </div>
  );
};

export default CardPayDialog;
